/** Lead fields that must not appear in Lead → object field mapping (system / conversion lookups). */
const EXCLUDED_BY_NAME = new Set([
  'id',
  'createddate',
  'createdbyid',
  'lastmodifiedbyid',
  'lastmodifieddate',
  'is_deleted',
  'is_converted',
  'company',
  'converted_account',
  'converted_contact',
  'converted_opportunity',
  'convertedaccountid',
  'convertedcontactid',
  'convertedopportunityid',
]);

const EXCLUDED_BY_LABEL = new Set([
  'converted account',
  'converted contact',
  'converted opportunity',
]);

/**
 * Decide whether a lead field should be hidden from the Lead → object field mapping UI.
 *
 * Pass `{ allowIdField: true }` to make the lead `id` field eligible for mapping.
 * Use this when the admin opts not to mark the lead as converted, so the converted
 * record can keep a lookup back to the original lead.
 */
export function isLeadFieldExcludedFromMapping(field, options = {}) {
  if (!field) return true;
  const name = String(field.field_name || '').toLowerCase();
  if (name === 'id' && options.allowIdField) {
    return false;
  }
  if (EXCLUDED_BY_NAME.has(name)) return true;
  const label = String(field.field_label || '').toLowerCase().trim();
  if (EXCLUDED_BY_LABEL.has(label)) return true;
  return false;
}

/** Account, Contact, Opportunity use built-in conversion for lead first/last name — no manual mapping. */
export const CORE_LEAD_CONVERSION_TARGET_KEYS = new Set(['account', 'contact', 'opportunity']);

export function isLeadFirstNameOrLastName(field) {
  const name = String(field?.field_name || '').toLowerCase();
  return name === 'first_name' || name === 'last_name';
}

export function isStandardConversionSkipsLeadNameMapping(field, targetKey) {
  if (!field || targetKey == null) return false;
  const key = String(targetKey).toLowerCase();
  if (!CORE_LEAD_CONVERSION_TARGET_KEYS.has(key)) return false;
  return isLeadFirstNameOrLastName(field);
}

/** Collect comparable strings from a detail-view picklist / scalar value. */
export function getComparableLeadStatusStrings(raw) {
  const out = [];
  const pushScalar = (v) => {
    if (v == null || v === '') return;
    out.push(String(v));
  };
  const walk = (v) => {
    if (v == null || v === '') return;
    if (typeof v === 'object' && !Array.isArray(v)) {
      pushScalar(v.value ?? v.displayValue);
      pushScalar(v.label ?? v.displayLabel);
      const inner = v.value ?? v.displayValue;
      if (inner != null && typeof inner === 'object') {
        walk(inner);
      }
      return;
    }
    pushScalar(v);
  };
  walk(raw);
  return [...new Set(out.map((s) => s.trim().toLowerCase()).filter(Boolean))];
}

function buildLeadConvertStatusMatchSet(config) {
  if (!config || typeof config !== 'object') return new Set();
  const out = new Set();
  const vk = config.valueKey != null ? String(config.valueKey).trim().toLowerCase() : '';
  const lab = config.label != null ? String(config.label).trim().toLowerCase() : '';
  if (vk) out.add(vk);
  if (lab) out.add(lab);
  return out;
}

/** True when the lead's current status matches the configured "lead convert" status (key or label). */
export function leadStatusMatchesLeadConvertStatus(raw, config) {
  const set = buildLeadConvertStatusMatchSet(config);
  if (set.size === 0) return false;
  for (const s of getComparableLeadStatusStrings(raw)) {
    if (set.has(s)) return true;
  }
  return false;
}

/**
 * Normalize lead_convert_status from API `data` (GET /lead/conversion-config) or legacy array.
 * @returns {{ valueKey: string, label: string } | null}
 */
export function normalizeLeadConvertStatusFromApiData(data) {
  if (!data || typeof data !== 'object') return null;
  const raw = data.leadConvertStatus;
  if (raw && typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const valueKey = raw.valueKey != null ? String(raw.valueKey).trim() : '';
    const label = raw.label != null ? String(raw.label).trim() : '';
    if (!valueKey && !label) return null;
    return { valueKey, label };
  }
  if (typeof raw === 'string' && raw.trim()) {
    return { valueKey: '', label: raw.trim() };
  }
  const legacy = data.hideConvertLeadStatuses;
  if (Array.isArray(legacy) && legacy[0] != null && String(legacy[0]).trim()) {
    return { valueKey: '', label: String(legacy[0]).trim() };
  }
  return null;
}

/** Same as normalizeLeadConvertStatusFromApiData but for `config.lead_conversion_config`. */
export function normalizeLeadConvertStatusFromLeadConversionConfig(leadConversionConfig) {
  return normalizeLeadConvertStatusFromApiData(leadConversionConfig || {});
}

/** Form.Select value (picklist value_key) from stored config + options. */
export function leadConvertStatusSelectKeyFromStored(storedConfig, picklistOptions) {
  const opts = Array.isArray(picklistOptions) ? picklistOptions : [];
  if (!storedConfig || typeof storedConfig !== 'object') return '';
  const vk = String(storedConfig.valueKey || '').trim();
  if (vk && opts.some((o) => String(o.key) === vk)) return vk;
  const lab = String(storedConfig.label || '').trim();
  const byLabel = opts.find((o) => String(o.value) === lab);
  return byLabel ? String(byLabel.key) : '';
}
